import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useState } from "react";
import { Menu, X, Phone, Mail, Instagram, Globe } from "lucide-react";
import { useContent } from "@/contexts/ContentContext";

const LOGO = "https://customer-assets.emergentagent.com/job_ff078f22-bc2b-467a-b845-6483b385dad8/artifacts/c2lq75mw_image.png";

const NAV = [
    { to: "/", label: "Accueil", testid: "nav-accueil" },
    { to: "/a-propos", label: "À propos", testid: "nav-apropos" },
    { to: "/services", label: "Services", testid: "nav-services" },
    { to: "/galerie", label: "Galerie", testid: "nav-galerie" },
    { to: "/menus", label: "Menus", testid: "nav-menus" },
    { to: "/contact", label: "Contact", testid: "nav-contact" },
];

export function Header() {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const loc = useLocation();

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 30);
        onScroll();
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        setOpen(false);
    }, [loc.pathname]);

    return (
        <header
            data-testid="site-header"
            className={`fixed top-0 left-0 right-0 z-50 transition-all duration-500 ${
                scrolled ? "bg-kayama-cream/95 backdrop-blur-xl border-b border-kayama-line shadow-[0_4px_20px_rgba(62,39,35,0.05)]" : "bg-transparent"
            }`}
        >
            <div className="container-k flex items-center justify-between py-4">
                <Link to="/" data-testid="header-logo-link" className="flex items-center gap-3 shrink-0">
                    <img src={LOGO} alt="Kayama Saveurs" className="h-12 md:h-14 w-auto object-contain" />
                    <span className="sr-only">Kayama Saveurs</span>
                </Link>

                <nav className="hidden lg:flex items-center gap-9">
                    {NAV.map((n) => (
                        <NavLink
                            key={n.to}
                            to={n.to}
                            end={n.to === "/"}
                            data-testid={n.testid}
                            className={({ isActive }) => `link-gold ${isActive ? "active" : ""}`}
                        >
                            {n.label}
                        </NavLink>
                    ))}
                </nav>

                <div className="hidden lg:flex items-center gap-3">
                    <Link to="/contact" data-testid="header-cta-devis" className="btn-primary-k">
                        Demander un devis
                    </Link>
                </div>

                <button
                    onClick={() => setOpen((v) => !v)}
                    data-testid="mobile-menu-toggle"
                    className="lg:hidden p-2 text-kayama-brown"
                    aria-label="Menu"
                >
                    {open ? <X size={26} /> : <Menu size={26} />}
                </button>
            </div>

            {open && (
                <div className="lg:hidden bg-kayama-cream border-t border-kayama-line" data-testid="mobile-menu">
                    <nav className="container-k py-6 flex flex-col gap-5">
                        {NAV.map((n) => (
                            <NavLink
                                key={n.to}
                                to={n.to}
                                end={n.to === "/"}
                                data-testid={`mobile-${n.testid}`}
                                className={({ isActive }) => `link-gold ${isActive ? "active" : ""}`}
                            >
                                {n.label}
                            </NavLink>
                        ))}
                        <Link to="/contact" data-testid="mobile-cta-devis" className="btn-primary-k justify-center mt-2">
                            Demander un devis
                        </Link>
                    </nav>
                </div>
            )}
        </header>
    );
}

export function Footer() {
    const c = useContent();
    const ct = c.contact;
    return (
        <footer data-testid="site-footer" className="bg-kayama-brown text-kayama-cream mt-32">
            <div className="container-k py-20 grid md:grid-cols-4 gap-12">
                <div className="md:col-span-2">
                    <img src={LOGO} alt="Kayama Saveurs" className="h-20 w-auto bg-kayama-cream p-2" />
                    <p className="mt-6 font-heading text-2xl font-light text-kayama-cream/90 max-w-md leading-snug">
                        {c.brand.footer_tagline}
                    </p>
                    <p className="script text-4xl mt-6 text-kayama-gold-light">{c.brand.script_gold}</p>
                </div>

                <div>
                    <div className="overline text-kayama-gold mb-5">Contact</div>
                    <ul className="space-y-3 text-sm text-kayama-cream/85">
                        <li className="flex items-center gap-3" data-testid="footer-phone">
                            <Phone size={16} className="text-kayama-gold" />
                            <a href={`tel:${ct.phone_href}`} className="hover:text-white">{ct.phone}</a>
                        </li>
                        <li className="flex items-center gap-3" data-testid="footer-email">
                            <Mail size={16} className="text-kayama-gold" />
                            <a href={`mailto:${ct.email}`} className="hover:text-white">{ct.email}</a>
                        </li>
                        <li className="flex items-center gap-3" data-testid="footer-instagram">
                            <Instagram size={16} className="text-kayama-gold" />
                            <a href={ct.instagram_url} target="_blank" rel="noreferrer" className="hover:text-white">{ct.instagram_handle}</a>
                        </li>
                        {ct.website_label && (
                            <li className="flex items-center gap-3" data-testid="footer-website">
                                <Globe size={16} className="text-kayama-gold" />
                                <a href={ct.website_url || `https://${ct.website_label}`} target="_blank" rel="noreferrer" className="hover:text-white">{ct.website_label}</a>
                            </li>
                        )}
                    </ul>
                </div>

                <div>
                    <div className="overline text-kayama-gold mb-5">Navigation</div>
                    <ul className="space-y-3 text-sm text-kayama-cream/85">
                        {NAV.map((n) => (
                            <li key={n.to}>
                                <Link to={n.to} className="hover:text-kayama-gold transition-colors" data-testid={`footer-${n.testid}`}>
                                    {n.label}
                                </Link>
                            </li>
                        ))}
                    </ul>
                </div>
            </div>

            <div className="border-t border-kayama-cream/10">
                <div className="container-k py-6 flex flex-col md:flex-row items-center justify-between gap-3 text-xs text-kayama-cream/60">
                    <span>© {new Date().getFullYear()} Kayama Saveurs · Tous droits réservés.</span>
                </div>
            </div>
        </footer>
    );
}

export default function Layout({ children }) {
    return (
        <div className="min-h-screen flex flex-col bg-kayama-cream">
            <Header />
            <main className="flex-1 pt-24">{children}</main>
            <Footer />
        </div>
    );
}
